/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3120Country, U3120CustomerType, U3120State } from "../u3120Customer/U3120Service";
import type {
  U3121InitLUPersonalization,
  U3121Project,
  U3121SubProject,
  U3121Task,
  U3121WorkNature,
} from "./U3121Service";

export type InitLU = U2000LUState["personalization"] & {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  tasks?: U3121Task[];
  workNatures?: U3121WorkNature[];
  customerTypes?: U3120CustomerType[];
  defaultCountry?: U3120Country[];
  defaultState?: U3120State[];
  /** BL-0495 — gate onglet Documents / scan NAS. */
  enableGedNas?: boolean;
  personalization?: U3121InitLUPersonalization;
};

export interface U3121ContextType {
  initLU: InitLU;
  /** Relance GET /initLU (ex. après sauvegarde d’un template d’assignation, BL-0371). */
  refreshInitLU: () => void;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3121Project>;
  gridProjectSubProjectStateHandlers: U2000GridStateHandlersReturn<U3121SubProject>;
  /** Onglet actif du formulaire projet (conservé à la navigation vers sous-projet et au retour). */
  projectCurrentTab: string | null;
  setProjectCurrentTab: (tabId: string) => void;
}

interface U3121ContextProviderProps {
  initLU: InitLU;
  refreshInitLU: () => void;
  children: ReactNode;
}

const U3121Context = React.createContext<U3121ContextType | undefined>(undefined);

export const U3121ContextProvider: React.FC<U3121ContextProviderProps> = ({ initLU, refreshInitLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3121",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3121Project>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });
  const U3121GridProjectSubProjectStateHandlers = useU2000GridStateHandlers<U3121SubProject>();
  const [projectCurrentTab, setProjectCurrentTab] = React.useState<string | null>(null);

  const contextValue = useMemo<U3121ContextType>(
    () => ({
      initLU,
      refreshInitLU,
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      gridProjectSubProjectStateHandlers: U3121GridProjectSubProjectStateHandlers,
      projectCurrentTab,
      setProjectCurrentTab,
    }),
    [initLU, refreshInitLU, U2000LUStateHandlers, U2000SearchStateHandlers, U2000GridStateHandlers, U3121GridProjectSubProjectStateHandlers, projectCurrentTab],
  );

  return <U3121Context.Provider value={contextValue}>{children}</U3121Context.Provider>;
};

export function useU3121Context(): U3121ContextType {
  const context = useContext(U3121Context);
  if (!context) {
    throw new Error("useU3121Context must be used within a U3121ContextProvider");
  }
  return context;
}
